// useSettings — orchestration hook for the Settings shell (CHG-1239).
//
// Owns the data lifecycle (useQuery fetch, useMutation save) and the local
// edit-vs-server diff so the page component stays a thin consumer. Per
// .forge/plans/v2-settings-shell.md §6 risk: pushed here to keep Settings.tsx
// ≤ ~25 LOC.

import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { settingsApi, type SettingsValues } from "../api/settings";
import { useOrg } from "../contexts/OrgContext";

// CHG-1648 / ITER-3757 — STUB_ORG_ID="current" literal removed.
// GET /api/v1/organizations/current/settings 404s because "current" is
// not a UUID and the FastAPI path param coerces it to str without any
// "current" → active-org bridging. The fix wires the real active org id
// from OrgContext.

export interface UseSettingsResult {
  values: SettingsValues;
  onChange: (sectionId: string, fieldId: string, value: unknown) => void;
  onSaveSection: (sectionId: string) => Promise<void>;
  dirty: Record<string, boolean>;
  busy: boolean;
  error?: string;
}

export function useSettings(): UseSettingsResult {
  const queryClient = useQueryClient();
  const { org } = useOrg();
  const orgId = org?.id ?? null;
  const { data: serverValues = {}, isLoading } = useQuery({
    queryKey: ["settings", orgId],
    queryFn: () => settingsApi.getAll(orgId as string),
    // Skip the fetch entirely while there's no active org — the
    // Settings page only renders inside the staff-role shell where
    // OrgContext eventually populates an active org; the query
    // re-runs automatically when orgId flips from null to a uuid.
    enabled: !!orgId,
  });

  const [edits, setEdits] = useState<SettingsValues>({});
  const values = useMemo<SettingsValues>(() => mergeValues(serverValues, edits), [serverValues, edits]);
  const dirty = useMemo(() => computeDirty(serverValues, edits), [serverValues, edits]);

  const mutation = useMutation({
    mutationFn: ({ sectionId, values: payload }: { sectionId: string; values: Record<string, unknown> }) =>
      settingsApi.patchSection(orgId as string, sectionId, payload),
    onSuccess: (_, { sectionId }) => {
      setEdits((prev) => {
        const next = { ...prev };
        delete next[sectionId];
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ["settings", orgId] });
    },
  });

  return {
    values,
    dirty,
    busy: mutation.isPending || isLoading,
    // Surface a no-active-org state to the page so the section rail
    // can render a "Pick an org first" notice instead of an empty
    // shell during the OrgContext bootstrap window.
    error: mutation.error
      ? (mutation.error as Error).message
      : orgId === null && !isLoading
        ? "No organization selected"
        : undefined,
    onChange: (sectionId, fieldId, value) =>
      setEdits((prev) => ({ ...prev, [sectionId]: { ...(prev[sectionId] ?? {}), [fieldId]: value } })),
    onSaveSection: async (sectionId) => {
      if (!orgId) throw new Error("No organization selected");
      await mutation.mutateAsync({ sectionId, values: values[sectionId] ?? {} });
    },
  };
}

function mergeValues(server: SettingsValues, edits: SettingsValues): SettingsValues {
  const merged: SettingsValues = {};
  const keys = new Set([...Object.keys(server), ...Object.keys(edits)]);
  for (const sectionId of keys) {
    merged[sectionId] = { ...(server[sectionId] ?? {}), ...(edits[sectionId] ?? {}) };
  }
  return merged;
}

function computeDirty(server: SettingsValues, edits: SettingsValues): Record<string, boolean> {
  const dirty: Record<string, boolean> = {};
  for (const [sectionId, sectionEdits] of Object.entries(edits)) {
    const serverSection = server[sectionId] ?? {};
    dirty[sectionId] = Object.entries(sectionEdits).some(
      ([k, v]) => !Object.is(v, serverSection[k]),
    );
  }
  return dirty;
}
